import React, { useContext, useEffect, useState } from "react";
import UserContext from "context/User";
import { getProjectPurchaseOrders } from "api/PurchaseOrder";
import { themevals } from "theme/themevals";

/**
 * Share with a provider (2026-10-20). Providers see a note, file or photo
 * only when staff pick them here; everything else stays hidden from them.
 * Lists the providers that have a PO on the project.
 */

const cache: Record<string, Promise<Array<{ id: string; name: string }>>> = {};

const providersFor = (projectId: string) => {
  if (!cache[projectId]) {
    cache[projectId] = getProjectPurchaseOrders({ data: { id: projectId } }).then((res: any) => {
      const list: any[] = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
      const seen = new Map<string, string>();
      list.forEach((po: any) => {
        if (po?.provider?.id && Number(po?.status) !== 6) seen.set(po.provider.id, po.provider.name || "Provider");
      });
      return Array.from(seen, ([id, name]) => ({ id, name }));
    });
  }
  return cache[projectId];
};

interface Props {
  projectId: string;
  value: string | null | undefined;
  onChange: (providerId: string | null) => Promise<void> | void;
}

const ProviderShareSelect: React.FC<Props> = ({ projectId, value, onChange }) => {
  const [providers, setProviders] = useState<Array<{ id: string; name: string }> | null>(null);
  const [saving, setSaving] = useState(false);
  const { user } = useContext<any>(UserContext);
  const staff = user?.accessLevel === "isCompany" || user?.accessLevel === "isSuper";

  useEffect(() => {
    let live = true;
    if (projectId && staff) providersFor(projectId).then((p) => live && setProviders(p)).catch(() => live && setProviders([]));
    return () => {
      live = false;
    };
  }, [projectId, staff]);

  if (!staff || !providers || (providers.length === 0 && !value)) return null;

  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey }}>
      Provider:
      <select
        value={value || ""}
        disabled={saving}
        onChange={async (e) => {
          setSaving(true);
          try {
            await onChange(e.target.value || null);
          } finally {
            setSaving(false);
          }
        }}
        style={{ fontFamily: "Poppins", fontSize: "12px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "4px", padding: "2px 4px", background: "white" }}
      >
        <option value="">Not shared</option>
        {providers.map((p) => (
          <option key={p.id} value={p.id}>
            Shared with {p.name}
          </option>
        ))}
      </select>
    </label>
  );
};

export default ProviderShareSelect;
